/**
 * Global Error Context
 * Centralized error state management with toast/snackbar notification system.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import apiErrorEmitter from '../services/apiErrorEmitter';

// ---- Types ----

export type ErrorSeverity = 'error' | 'warning' | 'info';

export type ErrorType = 'network' | 'auth' | 'forbidden' | 'validation' | 'server' | 'unknown';

export interface AppError {
  id: string;
  message: string;
  severity: ErrorSeverity;
  type: ErrorType;
  timestamp: number;
  details?: string;
}

export interface ErrorContextType {
  /** Current list of active errors (for the snackbar queue). */
  errors: AppError[];
  /** The most recent error to display in the snackbar. */
  currentError: AppError | null;
  /** Push a new error notification. */
  addError: (message: string, type?: ErrorType, severity?: ErrorSeverity, details?: string) => void;
  /** Dismiss a specific error by id. */
  dismissError: (id: string) => void;
  /** Dismiss all errors. */
  clearErrors: () => void;
  /** Convenience helpers. */
  showNetworkError: (message?: string) => void;
  showAuthError: (message?: string) => void;
  showForbiddenError: (message?: string) => void;
  showValidationError: (message: string) => void;
  showServerError: (message?: string) => void;
}

// ---- Helpers ----

let errorIdCounter = 0;

function createErrorId(): string {
  errorIdCounter += 1;
  return `err_${Date.now()}_${errorIdCounter}`;
}

// ---- Context ----

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

// ---- Provider ----

export const ErrorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [errors, setErrors] = useState<AppError[]>([]);

  const addError = useCallback(
    (
      message: string,
      type: ErrorType = 'unknown',
      severity: ErrorSeverity = 'error',
      details?: string
    ) => {
      const newError: AppError = {
        id: createErrorId(),
        message,
        severity,
        type,
        timestamp: Date.now(),
        details,
      };
      setErrors((prev) => [...prev, newError]);
    },
    []
  );

  // Subscribe to API error events emitted by the Axios interceptor
  useEffect(() => {
    const unsubscribe = apiErrorEmitter.subscribe((event) => {
      addError(event.message, event.type, event.severity, event.details);
    });
    return unsubscribe;
  }, [addError]);

  const dismissError = useCallback((id: string) => {
    setErrors((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  // Convenience helpers
  const showNetworkError = useCallback(
    (message = 'Network connection error. Please check your internet connection.') => {
      addError(message, 'network', 'warning');
    },
    [addError]
  );

  const showAuthError = useCallback(
    (message = 'Your session has expired. Please log in again.') => {
      addError(message, 'auth', 'warning');
    },
    [addError]
  );

  const showForbiddenError = useCallback(
    (message = 'You do not have permission to perform this action.') => {
      addError(message, 'forbidden', 'error');
    },
    [addError]
  );

  const showValidationError = useCallback(
    (message: string) => {
      addError(message, 'validation', 'warning');
    },
    [addError]
  );

  const showServerError = useCallback(
    (message = 'An unexpected server error occurred. Please try again later.') => {
      addError(message, 'server', 'error');
    },
    [addError]
  );

  // The most recent error is shown in the snackbar
  const currentError = errors.length > 0 ? errors[errors.length - 1] : null;

  const value: ErrorContextType = {
    errors,
    currentError,
    addError,
    dismissError,
    clearErrors,
    showNetworkError,
    showAuthError,
    showForbiddenError,
    showValidationError,
    showServerError,
  };

  return <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>;
};

// ---- Hook ----

export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

export default ErrorContext;
